import { DurationUnit } from "luxon";

import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Gender, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { HeightUnitConverter } from "@/Common/Utilities/Units/HeightUnitConverter";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { CorrectedAgeCalculator } from "../../Services/GrowthCharts/Calculators/CorrectedAgeCalculator";
import { AxisConfiguration } from "./ChartData";

type ChartId =
    | "FWFA"
    | "FLFA"
    | "FHCFA"
    | "WFA02"
    | "LFA02"
    | "WFL02"
    | "HCFA05"
    | "WFA219"
    | "HFA219"
    | "BMI219"
    | "VSW"
    | "VSH"
    | "VSBMI"
    | "VSPB"
    | "VSHR";

type VitalProperty =
    | "Weight"
    | "Height"
    | "HeadCircumference"
    | "MassBodyIndex"
    | "BPSys"
    | "BPDia"
    | "HeartRate";

type AgeUnit =
    | "week"
    | "month"
    | "year";

abstract class BaseChartModel {
    public readonly patient: Individual;
    protected readonly vitalSigns: Immutable.List<MedicalItemVitalSign>;
    protected readonly unit: UnitSystem;

    constructor(
        patient: Individual,
        vitals: Immutable.List<MedicalItemVitalSign>,
        unit?: UnitSystem
    ) {
        this.patient = patient;
        this.vitalSigns = vitals;
        this.unit = unit ?? UnitSystem.Metric;
    }

    public abstract isDisplayedByDefault(): boolean;
    public abstract isCompatible(): boolean;
    public abstract get isVitalSign(): boolean;
    public abstract get isPlotSelectionSupported(): boolean;

    public abstract get id(): ChartId;
    public abstract get name(): string;
    public abstract get shortName(): string;

    public abstract get ageUnit(): AgeUnit;
    public abstract get vitalProperty(): VitalProperty;

    public abstract get xAxis(): AxisConfiguration;
    public abstract get yAxis(): AxisConfiguration;

    public abstract filterVitalSign(vitalSign: MedicalItemVitalSign): boolean;

    public get isFentonChart(): boolean {
        return false;
    }

    public getVitalSignById(id: string): MedicalItemVitalSign {
        return this.vitalSigns?.toArray()?.firstOrDefault(p => p.MedicalItemId === id);
    }

    protected get gender(): Gender {
        return MyleHelper.genderLookupToEnum(this.patient.GenderLookupId);
    }

    protected convertVitalSignIfNeeded(field: number, weightUnitLookupId: string, heightUnitLookupId: string, headCircumferenceUnitLookupId: string): number {
        return this.convertVitalSignToCurrentUnitSystem(this.vitalProperty, field, weightUnitLookupId, heightUnitLookupId, headCircumferenceUnitLookupId);
    }

    protected convertVitalSignToCurrentUnitSystem(vitalProperty: VitalProperty, field: number, weightUnitLookupId: string, heightUnitLookupId: string, headCircumferenceUnitLookupId: string): number {
        switch (vitalProperty) {
            case "Weight":
                return this.getValueForWeight(field, weightUnitLookupId);
            case "Height":
                return this.getValueForHeight(field, heightUnitLookupId);
            case "HeadCircumference":
                return this.getValueForHeight(field, headCircumferenceUnitLookupId);
            default:
                return field;
        }
    }

    private getValueForWeight(value: number, unitLookupId: string): number {
        const kgLookupId = cacheStore.FindLookupByLookupCode("KILOGRAM", "WEIGHTTYPE").LookupID;
        const lbLookupId = cacheStore.FindLookupByLookupCode("POUND", "WEIGHTTYPE").LookupID;

        if (unitLookupId === kgLookupId && this.unit === UnitSystem.Imperial) {
            return WeightUnitConverter.kilogramToPound(value);
        }
        else if (unitLookupId === lbLookupId && this.unit === UnitSystem.Metric) {
            return WeightUnitConverter.poundToKilogram(value);
        }
        return value;
    }

    private getValueForHeight(value: number, unitLookupId: string): number {
        const cmLookupId = cacheStore.FindLookupByLookupCode("CENTIMETER", "HEIGHTTYPE").LookupID;
        const inLookupId = cacheStore.FindLookupByLookupCode("INCH", "HEIGHTTYPE").LookupID;

        if (unitLookupId === cmLookupId && this.unit === UnitSystem.Imperial) {
            return HeightUnitConverter.centimeterToInch(value);
        }
        else if (unitLookupId === inLookupId && this.unit === UnitSystem.Metric) {
            return HeightUnitConverter.inchToCentimeter(value);
        }
        return value;
    }

    public hasBornPrematureData(): boolean {
        return this.patient.IsBornPremature && !!this.patient.BornPrematureWeeks;
    }

    public getCorrectedAge(): number {
        const correctedAgeDate = CorrectedAgeCalculator.calculateCorrectedAge(this.patient.DateOfBirth, this.patient.BornPrematureWeeks);
        return LuxonDateHelper.dateDiff(correctedAgeDate, DateTime.nowDate(), "years" as DurationUnit);
    }
}

export { AgeUnit, BaseChartModel, ChartId, VitalProperty };
